import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { stockInApi } from 'api/endpoints/StockIn';
import { categoryApi } from 'api/endpoints/Category';
import { getTenantFromToken } from 'utils/routeUtils';

function CreateStockIn() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const tenant = getTenantFromToken();

  // Header states
  const [stockInDate, setStockInDate] = useState(
    new Date().toISOString().substring(0, 10)
  );
  const [organizationId, setOrganizationId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [refNo, setRefNo] = useState('');
  const [stockInCode, setStockInCode] = useState('');
  const [status, setStatus] = useState('draft');

  // Lists for dropdowns
  const [organizations, setOrganizations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);

  // Detail line items state
  const [items, setItems] = useState([
    {
      tempId: Date.now(),
      productCategoryId: '',
      productId: '',
      optionId: '',
      pricePerUnit: '',
      quantity: '',
      stockValue: 0,
      uom: '',
      productList: [],
      optionsList: []
    }
  ]);

  // UI/State controls
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load organizations, warehouses, and categories
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [orgRes, whRes, catRes] = await Promise.all([
          stockInApi.getOrganizations(),
          stockInApi.getWarehouses(),
          categoryApi.getCategoryList()
        ]);

        setOrganizations(orgRes.data?.results || orgRes.data || []);
        setWarehouses(whRes.data?.results || whRes.data || []);
        setCategories(catRes.data?.results || []);
      } catch (error) {
        console.error('Error fetching metadata:', error);
        toast.error('Failed to load initial form dropdowns');
      }
    };

    fetchMetadata();
  }, []);

  // Load existing stock-in entry for editing
  useEffect(() => {
    if (isEditMode) {
      const fetchStockInDetails = async () => {
        setLoading(true);
        try {
          const response = await stockInApi.getStockIn(id);
          const data = response.data?.results;
          if (data) {
            setStockInDate(data.stock_in_date);
            setOrganizationId(data.organization_id || '');
            setWarehouseId(data.warehouse_id || '');
            setRefNo(data.ref_no || '');
            setStockInCode(data.stock_in_code || '');
            setStatus(data.status || 'draft');

            // Map detail items and load products cascadingly
            if (data.items && data.items.length > 0) {
              const mappedItems = await Promise.all(
                data.items.map(async (item, index) => {
                  let products = [];
                  let options = [];
                  let selectedProduct = null;

                  if (item.product_category_id) {
                    try {
                      const prodRes = await stockInApi.getProductReport({
                        cursor: 0,
                        limit: 100,
                        filters: {
                          search_text: '',
                          start_date: '',
                          end_date: ''
                        },
                        category_id: item.product_category_id
                      });
                      products =
                        prodRes.data?.results?.data ||
                        prodRes.data?.results ||
                        [];
                      selectedProduct = products.find(
                        p => p.id === item.product_id
                      );
                    } catch (err) {
                      console.error(
                        'Failed to load products for category',
                        item.product_category_id,
                        err
                      );
                    }
                  }

                  if (selectedProduct) {
                    let productOptions = selectedProduct.options;
                    if (typeof productOptions === 'string') {
                      try {
                        productOptions = JSON.parse(productOptions);
                      } catch (e) {
                        console.error('Failed to parse options string:', e);
                      }
                    }
                    options = productOptions
                      ? Object.keys(productOptions).map(key => {
                          const optVal = productOptions[key];
                          let optLabel = '';
                          if (optVal && typeof optVal === 'object') {
                            optLabel =
                              optVal.option_name ||
                              optVal.name ||
                              optVal.label ||
                              key;
                          } else {
                            optLabel = String(optVal || key);
                          }
                          return {
                            value: key,
                            label: optLabel
                          };
                        })
                      : [];
                  }

                  return {
                    tempId: Date.now() + index,
                    productCategoryId: item.product_category_id || '',
                    productId: item.product_id || '',
                    optionId: item.option_id || '',
                    pricePerUnit: Number(item.price_per_unit) || 0,
                    quantity: Number(item.quantity) || 0,
                    stockValue: Number(item.stock_value) || 0,
                    uom: selectedProduct
                      ? selectedProduct.uom_name || selectedProduct.unit || ''
                      : '',
                    productList: products,
                    optionsList: options
                  };
                })
              );
              setItems(mappedItems);
            }
          }
        } catch (error) {
          console.error('Error fetching stock-in details:', error);
          toast.error('Failed to load stock-in entry details');
        } finally {
          setLoading(false);
        }
      };
      fetchStockInDetails();
    }
  }, [id, isEditMode]);

  // Add new item row to the table
  const handleAddItem = () => {
    setItems([
      ...items,
      {
        tempId: Date.now(),
        productCategoryId: '',
        productId: '',
        optionId: '',
        pricePerUnit: '',
        quantity: '',
        stockValue: 0,
        uom: '',
        productList: [],
        optionsList: []
      }
    ]);
  };

  // Remove item row from the table
  const handleRemoveItem = index => {
    if (items.length === 1) {
      toast.warning('At least one line item is required');
      return;
    }
    const newItems = items.filter((_, idx) => idx !== index);
    setItems(newItems);
  };

  // Cascading dropdowns - category change handler
  const handleCategoryChange = async (index, catId) => {
    const updatedItems = [...items];
    updatedItems[index].productCategoryId = catId;
    updatedItems[index].productId = '';
    updatedItems[index].optionId = '';
    updatedItems[index].pricePerUnit = 0;
    updatedItems[index].stockValue = 0;
    updatedItems[index].uom = '';
    updatedItems[index].productList = [];
    updatedItems[index].optionsList = [];

    if (catId) {
      try {
        const prodRes = await stockInApi.getProductReport({
          cursor: 0,
          limit: 100,
          filters: { search_text: '', start_date: '', end_date: '' },
          category_id: catId
        });
        updatedItems[index].productList =
          prodRes.data?.results?.data || prodRes.data?.results || [];
      } catch (error) {
        console.error('Error loading products for category:', error);
        toast.error('Failed to load products for selected category');
      }
    }
    setItems(updatedItems);
  };

  // Cascading dropdowns - product change handler
  const handleProductChange = (index, prodId) => {
    const updatedItems = [...items];
    updatedItems[index].productId = prodId;
    updatedItems[index].optionId = '';
    updatedItems[index].pricePerUnit = 0;
    updatedItems[index].uom = '';
    updatedItems[index].optionsList = [];

    const selectedProduct = updatedItems[index].productList.find(
      p => p.id === prodId
    );

    if (selectedProduct) {
      updatedItems[index].uom =
        selectedProduct.uom_name || selectedProduct.unit || '';

      // Parse product options if they exist
      let productOptions = selectedProduct.options;
      if (typeof productOptions === 'string') {
        try {
          productOptions = JSON.parse(productOptions);
        } catch (e) {
          console.error('Failed to parse options string:', e);
        }
      }

      if (productOptions && typeof productOptions === 'object') {
        const optsList = Object.keys(productOptions).map(key => {
          const optVal = productOptions[key];
          let optLabel = '';
          if (optVal && typeof optVal === 'object') {
            optLabel = optVal.option_name || optVal.name || optVal.label || key;
          } else {
            optLabel = String(optVal || key);
          }
          return {
            value: key,
            label: optLabel
          };
        });
        updatedItems[index].optionsList = optsList;
      }

      // If there are no options, fetch base price directly
      if (!productOptions || Object.keys(productOptions).length === 0) {
        updatedItems[index].pricePerUnit =
          Number(selectedProduct.base_price) || 0;
      }
    }

    // Recalculate row value
    updatedItems[index].stockValue =
      updatedItems[index].pricePerUnit * updatedItems[index].quantity;

    setItems(updatedItems);
  };

  // Cascading dropdowns - option change handler
  const handleOptionChange = (index, optId) => {
    const updatedItems = [...items];
    updatedItems[index].optionId = optId;

    const selectedProduct = updatedItems[index].productList.find(
      p => p.id === updatedItems[index].productId
    );

    if (selectedProduct && optId) {
      let prices = selectedProduct.option_prices;
      if (typeof prices === 'string') {
        try {
          prices = JSON.parse(prices);
        } catch (e) {
          console.error('Failed to parse option_prices string:', e);
        }
      }
      if (prices && typeof prices === 'object' && prices[optId] !== undefined) {
        updatedItems[index].pricePerUnit = Number(prices[optId]) || 0;
      } else {
        updatedItems[index].pricePerUnit =
          Number(selectedProduct.base_price) || 0;
      }
    } else if (selectedProduct) {
      updatedItems[index].pricePerUnit =
        Number(selectedProduct.base_price) || 0;
    }

    updatedItems[index].stockValue =
      updatedItems[index].pricePerUnit * updatedItems[index].quantity;

    setItems(updatedItems);
  };

  // Value change handlers for quantity and custom price overrides
  const handleItemValueChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value === '' ? '' : Number(value);
    const price = Number(updatedItems[index].pricePerUnit) || 0;
    const qty = Number(updatedItems[index].quantity) || 0;
    updatedItems[index].stockValue = price * qty;
    setItems(updatedItems);
  };

  // Calculations for display totals
  const subtotalItems = items.filter(
    item => item.productCategoryId && item.productId
  ).length;

  const totalQuantity = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  const totalStockInValue = items.reduce(
    (sum, item) => sum + (Number(item.stockValue) || 0),
    0
  );

  const formatUGX = val => {
    const numericVal = Number(val) || 0;
    return `UGX ${Math.round(numericVal).toLocaleString()}`;
  };

  // Form submission handler
  const handleSubmit = async (e, submitStatus) => {
    if (e) e.preventDefault();

    if (!organizationId) {
      toast.error('Organization is required');
      return;
    }
    if (!warehouseId) {
      toast.error('Warehouse/Center is required');
      return;
    }

    // Validate details list contains at least one completed row
    const validatedItems = items.filter(
      item =>
        item.productCategoryId && item.productId && Number(item.quantity) > 0
    );

    if (validatedItems.length === 0) {
      toast.error(
        'At least one completed product item with quantity > 0 is required'
      );
      return;
    }

    setSubmitting(true);

    const payload = {
      stock_in_date: stockInDate,
      organization_id: organizationId,
      warehouse_id: warehouseId,
      ref_no: refNo,
      status: submitStatus || status,
      items: validatedItems.map(item => ({
        product_category_id: item.productCategoryId,
        product_id: item.productId,
        option_id: item.optionId || null,
        price_per_unit: Number(item.pricePerUnit) || 0,
        quantity: Number(item.quantity) || 0,
        stock_value: item.stockValue
      }))
    };

    try {
      let response;
      if (isEditMode) {
        response = await stockInApi.updateStockIn(id, payload);
      } else {
        response = await stockInApi.createStockIn(payload);
      }

      if (
        response.data?.success ||
        response.status === 201 ||
        response.status === 200
      ) {
        toast.success(
          response.data?.results || 'Stock In Entry saved successfully!'
        );
        navigate(`/${tenant}/stock-in-report`);
      } else {
        toast.error(response.data?.results || 'Failed to save Stock In entry');
      }
    } catch (error) {
      console.error('Error submitting Stock In form:', error);
      const errMsg =
        error.response?.data?.detail || error.message || 'Error occurred';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-4">
      {/* Header card with navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: '40px', height: '40px' }}
            onClick={() => navigate(`/${tenant}/stock-in-report`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <div>
            <h4 className="mb-1 text-dark fw-bold">
              {isEditMode ? 'Edit Stock In Entry' : 'Create Stock In Entry'}
            </h4>
            <div className="text-muted" style={{ fontSize: '13px' }}>
              Inventory &gt; Stock In &gt;{' '}
              <span className="text-primary">
                {isEditMode ? 'Edit' : 'New'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="shadow-sm border-0 py-5 text-center">
          <Card.Body>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading stock-in details...</p>
          </Card.Body>
        </Card>
      ) : (
        <Form onSubmit={e => handleSubmit(e, status)}>
          <Row className="g-4">
            {/* Left Column: Form Details */}
            <Col lg={9}>
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px' }}
              >
                <Card.Body className="p-4">
                  <h5 className="card-title fw-bold text-dark mb-4">
                    Entry General Details
                  </h5>
                  <Row className="g-3 mb-4">
                    {isEditMode && (
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label
                            className="fw-bold text-secondary mb-2"
                            style={{ fontSize: '13px' }}
                          >
                            Stock In ID
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={stockInCode}
                            disabled
                            style={{
                              borderRadius: '8px',
                              padding: '10px 14px',
                              border: '1px solid #D8E0F0',
                              backgroundColor: '#F8FAFC'
                            }}
                          />
                        </Form.Group>
                      </Col>
                    )}
                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Stock In Date <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={stockInDate}
                          onChange={e => setStockInDate(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Organization <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={organizationId}
                          onChange={e => setOrganizationId(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Organization</option>
                          {organizations.map(org => (
                            <option
                              key={org.organization_id || org.value}
                              value={org.organization_id || org.value}
                            >
                              {org.organization_name || org.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={isEditMode ? 3 : 4}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Warehouse/Center{' '}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={warehouseId}
                          onChange={e => setWarehouseId(e.target.value)}
                          required
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        >
                          <option value="">Select Warehouse/Center</option>
                          {warehouses.map(wh => (
                            <option
                              key={wh.id || wh.value}
                              value={wh.id || wh.value}
                            >
                              {wh.name || wh.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label
                          className="fw-bold text-secondary mb-2"
                          style={{ fontSize: '13px' }}
                        >
                          Ref Number (Delivery Note / Invoice)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter reference invoice number"
                          value={refNo}
                          onChange={e => setRefNo(e.target.value)}
                          style={{
                            borderRadius: '8px',
                            padding: '10px 14px',
                            border: '1px solid #D8E0F0'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Detail Items card */}
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px' }}
              >
                <Card.Body className="p-4">
                  <h5 className="card-title fw-bold text-dark mb-4">
                    Stock In Items
                  </h5>
                  <div className="table-responsive">
                    <Table hover className="align-middle mt-2 mb-4">
                      <thead className="bg-light border-0">
                        <tr>
                          <th
                            style={{ fontSize: '12px', width: '22%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Category
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '22%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Product
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '18%' }}
                            className="text-secondary fw-bold py-3"
                          >
                            Option (Size/Variety)
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '14%' }}
                            className="text-secondary fw-bold py-3 text-end"
                          >
                            Price / Unit
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '10%' }}
                            className="text-secondary fw-bold py-3 text-center"
                          >
                            Qty
                          </th>
                          <th
                            style={{ fontSize: '12px', width: '14%' }}
                            className="text-secondary fw-bold py-3 text-end"
                          >
                            Stock Value
                          </th>
                          <th
                            style={{ width: '6%' }}
                            className="py-3 text-center"
                          ></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={item.tempId}>
                            <td>
                              <Form.Select
                                value={item.productCategoryId}
                                onChange={e =>
                                  handleCategoryChange(index, e.target.value)
                                }
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                  <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Select
                                value={item.productId}
                                onChange={e =>
                                  handleProductChange(index, e.target.value)
                                }
                                disabled={!item.productCategoryId}
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              >
                                <option value="">Select Product</option>
                                {item.productList.map(prod => (
                                  <option key={prod.id} value={prod.id}>
                                    {prod['Product Name'] ||
                                      prod.product_name ||
                                      prod.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Select
                                value={item.optionId}
                                onChange={e =>
                                  handleOptionChange(index, e.target.value)
                                }
                                disabled={
                                  !item.productId ||
                                  item.optionsList.length === 0
                                }
                                style={{
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                              >
                                <option value="">No Option Selected</option>
                                {item.optionsList.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </Form.Select>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                placeholder="Price"
                                value={item.pricePerUnit}
                                onChange={e =>
                                  handleItemValueChange(
                                    index,
                                    'pricePerUnit',
                                    e.target.value
                                  )
                                }
                                disabled={!item.productId}
                                style={{
                                  borderRadius: '6px',
                                  textAlign: 'right',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                              />
                              {item.productId && (
                                <div
                                  className="text-muted text-end mt-1"
                                  style={{ fontSize: '11px' }}
                                >
                                  per {item.uom || 'Unit'}
                                </div>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                min="0"
                                placeholder="Qty"
                                value={item.quantity}
                                onChange={e =>
                                  handleItemValueChange(
                                    index,
                                    'quantity',
                                    e.target.value
                                  )
                                }
                                disabled={!item.productId}
                                style={{
                                  borderRadius: '6px',
                                  textAlign: 'center',
                                  fontSize: '13px',
                                  padding: '8px'
                                }}
                                required
                              />
                            </td>
                            <td
                              className="text-end fw-semibold text-dark"
                              style={{ fontSize: '13px' }}
                            >
                              {formatUGX(item.stockValue)}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="link"
                                className="text-danger p-0 border-0"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <Button
                    variant="outline-primary"
                    className="rounded-pill d-flex align-items-center gap-2 mt-2"
                    onClick={handleAddItem}
                    style={{ fontSize: '13px', padding: '6px 16px' }}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Item Row
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Right Column: Totals summary cards */}
            <Col lg={3}>
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: '12px', backgroundColor: '#F8FAFC' }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold text-dark mb-4">
                    Stock Entry Summary
                  </h5>
                  <div className="d-flex justify-content-between mb-3">
                    <span
                      className="text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Line Items
                    </span>
                    <span className="fw-bold text-dark">{subtotalItems}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <span
                      className="text-secondary"
                      style={{ fontSize: '13px' }}
                    >
                      Total Quantity
                    </span>
                    <span className="fw-bold text-dark">{totalQuantity}</span>
                  </div>
                  <hr
                    className="my-3"
                    style={{ borderTop: '1px solid #E2E8F0' }}
                  />
                  <div className="mb-4">
                    <div
                      className="text-secondary mb-1"
                      style={{ fontSize: '13px' }}
                    >
                      Total Valuation
                    </div>
                    <h3 className="fw-bold text-primary mb-0">
                      {formatUGX(totalStockInValue)}
                    </h3>
                  </div>
                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      className="rounded-pill py-2 font-weight-bold"
                      type="button"
                      disabled={submitting}
                      onClick={e => handleSubmit(e, 'posted')}
                    >
                      {submitting ? 'Submitting...' : 'Confirm & Post Entry'}
                    </Button>
                    <Button
                      variant="light"
                      className="rounded-pill py-2"
                      disabled={submitting}
                      onClick={() => navigate(`/${tenant}/stock-in-report`)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      )}
    </Container>
  );
}

export default CreateStockIn;
